import { isEmpty, kebabCase, sortBy } from 'lodash';
import React, { Component } from 'react';
import config from '../../../config/config';
import {
  collegeMaxPlanLimits,
  PRODUCT_TYPE_LABEL_MAP,
} from '../../../settings/settings';
import {
  isRegistrationAssociatedWithBank,
  isRegistrationAssociatedWithModalId,
} from '../../../utils/banking/bank-utils';
import { isUserCountryRestricted } from '../../../utils/funds/funds-utility';
import { checkMissingFeaturesByCode } from '../../../utils/funds/shared/shared-fund-notes-utility';
import { disableIRAFunds } from '../../../utils/IRA/IRA-utility';
import {
  isCollegeContribute,
  isCollegeExchange,
} from '../../../utils/transactions-util';
import { coalesce } from '../../../utils/utils';
import ChoiceButton from '../../Shared/ChoiceButton/ChoiceButton';
import './FundRegistration.scss';

export class FundRegistration extends Component {
  constructor(props) {
    super(props);
    this.state = {
      started: true,
      disableIRAAgreements: false,
    };

    this.generateRegistrationButton =
      this.generateRegistrationButton.bind(this);
  }
  UNSAFE_componentWillMount() {
    const {
      IRALimit: { data },
      personalData,
    } = this.props;
    this.setState({
      disableIRAAgreements: disableIRAFunds(data, personalData),
    });
  }

  componentDidMount() {
    const { dispatchPageLoad } = this.props;
    dispatchPageLoad('Select New Registration');
  }

  sort(registrations) {
    return sortBy(registrations, ['agreementType', 'formattedEntityNames']);
  }

  onRegistrationButtonClick(registration) {
    const { setRegistrationTypes, setFundProgress } = this.props;
    setRegistrationTypes(registration);
    setFundProgress('FUND_SEARCH');
  }

  getFunds(groupedFunds, registration) {
    return groupedFunds && groupedFunds[registration.formattedEntityNames];
  }

  getTotalBalance(funds) {
    return funds
      ? funds.reduce((total, fund) => total + fund.balanceAsOf, 0)
      : null;
  }

  getMax(clgMaxPlanLimits, fund) {
    return (
      clgMaxPlanLimits[fund && fund.productType] || {
        max: 500000,
      }
    );
  }

  checkIfCollegeContributeAndCollecgeExchange() {
    return isCollegeContribute() || isCollegeExchange();
  }

  checkIfMaxBalanceReached(totalBalance, max) {
    return isCollegeContribute() && totalBalance && totalBalance > max;
  }

  getFundRegistrationDisabledMessage(registration, groupedFunds) {
    const {
      availableFundList,
      bucketRoll,
      personalData,
      selectedBankAssociableOrAssociatedLookup,
    } = this.props;
    const fund = availableFundList.find(
      (fund) => fund.formattedEntityNames === registration.formattedEntityNames
    );

    const funds = this.getFunds(groupedFunds, registration);
    const totalBalance = this.getTotalBalance(funds);

    if (
      registration.classification === 'RETIREMENT' &&
      bucketRoll.nextYearContributionAmountAvailable
    ) {
      return 'IRA Contributions are temporarily unavailable';
    }

    if (!registration.typeEligiblePurchaseNew) {
      return 'Ineligible for new purchases';
    }

    if (!isRegistrationAssociatedWithModalId(registration)) {
      return !this.checkIfCollegeContributeAndCollecgeExchange() ? (
        <>
          <a
            href="https://www.troweprice.com/personal-investing/help/contact/index.html"
            rel="noreferrer"
            target="_blank"
          >
            Contact us
          </a>{' '}
          to add investments to this account
        </>
      ) : (
        'Unavailable Online'
      );
    }

    if (
      !isRegistrationAssociatedWithBank(
        registration,
        selectedBankAssociableOrAssociatedLookup
      )
    ) {
      return 'Cannot link to chosen bank';
    }

    if (
      disableIRAFunds(fund, personalData) &&
      registration.classification === 'RETIREMENT' &&
      registration.agreementType.indexOf('IRA') > -1
    ) {
      return 'The minimum initial contribution of $1,000 required to open a new IRA fund exceeds your remaining annual contribution limit.';
    }

    if (isUserCountryRestricted(personalData)) {
      return 'Requires permanent US address';
    }

    if (isEmpty(registration.modelAgreementUniqueIDs)) {
      return 'Unable to link chosen bank';
    }

    if (registration.modelAgreementUniqueIDs.length === 1) {
      const modelFund = availableFundList.find(
        (modelFund) =>
          modelFund.uniqueKey === registration.modelAgreementUniqueIDs[0]
      );
      if (checkMissingFeaturesByCode(modelFund, 48)) {
        return 'Ineligible for purchases online';
      }
    }

    const { max } = this.getMax(collegeMaxPlanLimits, fund);
    if (this.checkIfMaxBalanceReached(totalBalance, max)) {
      return 'Account has reached maximum balance.';
    }
    return null;
  }

  generateRegistrationButton(registration, index, groupedFunds) {
    const nameReplacementChar =
      registration.agreementType === 'UGMA_UTMA' ? '/' : ' ';
    const registrationName = isCollegeContribute()
      ? coalesce(PRODUCT_TYPE_LABEL_MAP[registration.productType], '')
      : registration.agreementType.replace(/_/g, nameReplacementChar);
    const fundRegistrationDisabledMessage =
      this.getFundRegistrationDisabledMessage(registration, groupedFunds);
    return (
      <div
        id={`${kebabCase(registrationName)}-${kebabCase(
          registration.formattedEntityNames
        )}`}
        key={index}
      >
        <ChoiceButton
          index={index}
          header={registrationName}
          subHeader={
            <span className="blocked_data">
              {registration.formattedEntityNames}
            </span>
          }
          buttonClick={() => this.onRegistrationButtonClick(registration)}
          disabledMessage={fundRegistrationDisabledMessage}
        />
      </div>
    );
  }

  render() {
    const {
      modalProps: {
        addType,
        fundType,
        groupTypeFragment,
        url = `${config.url}/personal-investing/tools/fund-research/account-finder`,
      },
      customerWebViewEnabled,
      isSalesforceRep,
      userRegistrationTypeInfo,
      availableFundList,
    } = this.props;

    const groupedFunds = {};

    if (availableFundList) {
      availableFundList.forEach((fund) => {
        if (!groupedFunds[fund.formattedEntityNames]) {
          groupedFunds[fund.formattedEntityNames] = [];
        }
        groupedFunds[fund.formattedEntityNames].push(fund);
      });
    }
    return (
      <div className="fund-registration-container">
        <h4 className="fund-registration-modalHeader e2e-add-new-fund-registration">
          Add a new {fundType} to an existing {addType}
        </h4>
        <div className="fund-registration-modalScroller">
          <div className="fund-registration-modalBody">
            <div className="fund-registration-accountHeader">
              Choose the {addType} you'd like to add a new {fundType} to:
            </div>
            <div className="fund-registration-registration-buttons-grid">
              {this.sort(userRegistrationTypeInfo.registrationTypes).map(
                (reg, index) =>
                  this.generateRegistrationButton(reg, index, groupedFunds)
              )}
            </div>
          </div>
        </div>
        {!(!customerWebViewEnabled && isSalesforceRep) && (
          <span>
            <div className="fund-registration-modalFooter">
              Or,{' '}
              <a
                id="new-fund-add-acct-link"
                target="_blank"
                rel="noopener noreferrer"
                className="underline college-branded-link"
                href={url}
              >
                add a new {addType}
              </a>{' '}
              {groupTypeFragment}
            </div>
          </span>
        )}
      </div>
    );
  }
}

export default FundRegistration;
